export interface CrmTaskDetailEntry {
  label: string;
  value: string;
  truncate?: boolean;
}

interface CrmTaskOperatorBriefCardProps {
  entries: CrmTaskDetailEntry[];
}

interface CrmTaskCompletionDetailsCardProps {
  entries: CrmTaskDetailEntry[];
}

interface CrmTaskWhyExistsCardProps {
  context: string | null | undefined;
  templateLabel: string | null | undefined;
  guidance: string | null | undefined;
}

interface CrmTaskCompletionContextCardProps {
  context: string | null | undefined;
  guidance: string | null | undefined;
  brief: CrmTaskDetailEntry[];
}

interface CrmTaskOperationalDetailsCardProps {
  entries: CrmTaskDetailEntry[];
}

interface CrmTaskContextSnapshotCardProps {
  entries: CrmTaskDetailEntry[];
}

interface CrmTaskMetadataHighlightsCardProps {
  entries: CrmTaskDetailEntry[];
}

export function CrmTaskOperatorBriefCard({ entries }: CrmTaskOperatorBriefCardProps) {
  if (entries.length === 0) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Operator brief
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {entries.map((entry) => (
          <span
            key={`${entry.label}:${entry.value}`}
            className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]"
          >
            <span className="font-medium text-[var(--color-foreground)]">{entry.label}:</span> {entry.value}
          </span>
        ))}
      </div>
    </div>
  );
}

export function CrmTaskCompletionDetailsCard({ entries }: CrmTaskCompletionDetailsCardProps) {
  if (entries.length === 0) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Completion details
      </p>
      <dl className="mt-2 grid gap-2 sm:grid-cols-2">
        {entries.map((entry) => (
          <div
            key={`${entry.label}:${entry.value}`}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1"
          >
            <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
              {entry.label}
            </dt>
            <dd className="mt-0.5 text-xs text-[var(--color-foreground)]">{entry.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function CrmTaskWhyExistsCard({ context, templateLabel, guidance }: CrmTaskWhyExistsCardProps) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Why this task exists
      </p>
      <p className="mt-1 text-[var(--color-foreground)]">
        {context ?? "No extra context has been attached to this task yet."}
      </p>
      {templateLabel && (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Template: {templateLabel}
        </p>
      )}
      {guidance && (
        <div className="mt-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Suggested guidance
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-foreground)]">{guidance}</p>
        </div>
      )}
    </div>
  );
}

export function CrmTaskCompletionContextCard({
  context,
  guidance,
  brief,
}: CrmTaskCompletionContextCardProps) {
  if (!context && !guidance && brief.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Task context
      </p>
      {context && (
        <p className="mt-1 text-sm text-[var(--color-foreground)]">{context}</p>
      )}
      {guidance && (
        <div className="mt-2 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2.5 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)]">
            Staff action
          </p>
          <p className="mt-1 text-sm text-[var(--color-foreground)]">{guidance}</p>
        </div>
      )}
      {brief.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {brief.map((entry) => (
            <span
              key={`${entry.label}:${entry.value}`}
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]"
            >
              <span className="font-medium text-[var(--color-foreground)]">{entry.label}:</span> {entry.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function CrmTaskOperationalDetailsCard({ entries }: CrmTaskOperationalDetailsCardProps) {
  if (entries.length === 0) return null;

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Operational details
      </p>
      <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {entries.map((entry) => (
          <CrmTaskOperationalDetailEntry key={`${entry.label}:${entry.value}`} entry={entry} />
        ))}
      </dl>
    </div>
  );
}

function CrmTaskOperationalDetailEntry({ entry }: { entry: CrmTaskDetailEntry }) {
  return (
    <>
      <dt className="text-[var(--color-muted-foreground)]">{entry.label}</dt>
      <dd className={entry.truncate ? "truncate text-[var(--color-foreground)]" : "text-[var(--color-foreground)]"}>
        {entry.value}
      </dd>
    </>
  );
}

export function CrmTaskContextSnapshotCard({ entries }: CrmTaskContextSnapshotCardProps) {
  if (entries.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Context snapshot
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {entries.map((entry) => (
          <CrmTaskDetailChip key={`${entry.label}:${entry.value}`} entry={entry} />
        ))}
      </div>
    </div>
  );
}

export function CrmTaskMetadataHighlightsCard({ entries }: CrmTaskMetadataHighlightsCardProps) {
  if (entries.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {entries.map((entry) => (
        <CrmTaskDetailChip key={`${entry.label}:${entry.value}`} entry={entry} />
      ))}
    </div>
  );
}

function CrmTaskDetailChip({ entry }: { entry: CrmTaskDetailEntry }) {
  return (
    <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
      {entry.label}: {entry.value}
    </span>
  );
}
